import {gsap} from "gsap/all";

const SVG_NS = "http://www.w3.org/2000/svg";

// Collision-proof id: a module-level counter alone isn't safe here, since a
// bundler can end up including this module more than once (separate chunks/
// entry points), giving each copy its own counter restarting at 0 — which
// then produces duplicate gradient ids across the page and makes fill="url(#..)"
// resolve to the wrong element's gradient.
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Draws just the glow behind a pill-shaped button, sized to the measured
// button so it stays perfectly round at any width. Use this when the pill
// itself is a static included SVG (e.g. crew-pill-button) and only the
// glow needs to be generated/animated — for the full version (glow + pill
// shape) see PillButton.js. Supports both the default pill-button cap
// layout and the mirrored crew-pill-button layout (see buildPath).
export class PillGlow {

  // Default (PillButton-style) pill: small cap on the LEFT, big cap
  // (H/2) on the RIGHT. Ratio measured off the original pill-button SVG.
  static LEFT_RADIUS_RATIO = 52 / 140;

  // crew-pill-button's static SVG is the same idea MIRRORED: big cap
  // (H/2) on the LEFT, small cap on the RIGHT. Ratio measured off
  // images/build-your-team/crew/pill-button.svg (846x170 viewBox —
  // right cap radius ~70.9, left cap radius ~H/2).
  static CREW_RIGHT_RADIUS_RATIO = 0.417;

  // Default glow colors (the chromo gradient): teal -> pink -> orange.
  static DEFAULT_GLOW_COLORS = ["#49BACD", "#F36688", "#F9804F"];

  constructor(root) {
    this.root = root;
    root.pillGlow = this; // so callers can reach the instance from the element
    this.inner = root.querySelector(".inner");
    this.host = root.querySelector(".glow");
    if (!this.inner || !this.host) return;

    this.rounded = root.classList.contains("rounded"); // equal caps, symmetric pill
    this.crew = root.classList.contains("crew-pill-button"); // mirrored small-right cap
    this.id = uid();
    this.layer = this.buildLayer(this.host);
    if (!this.layer) return;

    // Redraw when the width changes: a language switch (label text), or a
    // button that started hidden becoming visible. Not for window resizing.
    this.observer = new ResizeObserver(() => this.draw());
    this.observer.observe(this.inner);
    this.draw();
    this.animateGlow();
  }

  // Slowly rotate the glow gradient so the colors swirl behind the pill.
  animateGlow() {
    if (!this.glowGradient) return;
    const state = {angle: 0};
    this.glowTween = gsap.to(state, {
      angle: 360,
      duration: 8,
      ease: "none",
      repeat: -1,
      onUpdate: () => {
        this.glowGradient.setAttribute("gradientTransform", `rotate(${state.angle} 0.5 0.5)`);
      },
    });
  }

  // Glow colors from the data-glow-colors attribute, or the default chromo.
  glowColors() {
    const attr = this.root.dataset.glowColors;
    if (attr) return attr.split(",").map((c) => c.trim()).filter(Boolean);
    return PillGlow.DEFAULT_GLOW_COLORS;
  }

  // (Re)build the gradient stops, spread evenly across the gradient (horizontal).
  applyGlowColors(grad) {
    grad.textContent = "";
    const colors = this.glowColors();
    colors.forEach((color, i) => {
      const stop = document.createElementNS(SVG_NS, "stop");
      stop.setAttribute("offset", colors.length > 1 ? `${i / (colors.length - 1)}` : "0");
      stop.setAttribute("stop-color", color);
      grad.appendChild(stop);
    });
  }

  // Repaint the glow after the colors changed. Pass an array (or a comma
  // separated string) to set new colors, or nothing to re-read the element's
  // data-glow-colors attribute.
  refreshGlow(colors = null) {
    if (colors) this.root.dataset.glowColors = Array.isArray(colors) ? colors.join(",") : colors;
    if (this.glowGradient) this.applyGlowColors(this.glowGradient);
  }

  // Repaint the glow of an element (element, selector or NodeList) after its
  // colors changed. Defaults to every crew-pill-button on the page.
  static refreshGlow(target = ".crew-pill-button", colors = null) {
    const els = typeof target === "string" ? document.querySelectorAll(target) : (target.length !== undefined ? target : [target]);
    for (const el of els) el.pillGlow?.refreshGlow(colors);
  }

  // Inject an <svg><path/></svg> into the .glow host; returns {svg, path} or null.
  buildLayer(host) {
    if (!host) return null;

    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("preserveAspectRatio", "none");
    const path = document.createElementNS(SVG_NS, "path");

    const gradId = `pill-glow-${this.id}`;
    const defs = document.createElementNS(SVG_NS, "defs");
    const grad = document.createElementNS(SVG_NS, "linearGradient");
    grad.setAttribute("id", gradId);
    grad.setAttribute("x1", "0");
    grad.setAttribute("y1", "0");
    grad.setAttribute("x2", "1");
    grad.setAttribute("y2", "0");

    this.applyGlowColors(grad);

    grad.setAttribute("gradientTransform", "rotate(0 0.5 0.5)");
    this.glowGradient = grad;
    defs.appendChild(grad);
    svg.appendChild(defs);
    path.setAttribute("fill", `url(#${gradId})`);

    svg.appendChild(path);
    host.appendChild(svg);
    return {svg, path};
  }

  draw() {
    const w = Math.round(this.inner.offsetWidth);
    const h = Math.round(this.inner.offsetHeight);
    if (w <= 0 || h <= 0 || !this.layer) return; // hidden / not laid out yet

    const d = PillGlow.buildPath(w, h, {rounded: this.rounded, crew: this.crew});
    const {svg, path} = this.layer;
    svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    path.setAttribute("d", d);
  }

  destroy() {
    this.observer?.disconnect();
    this.glowTween?.kill();
  }

  // Builds the pill from two rounded ends (caps) joined by straight lines. When
  // the two caps are different sizes the lines slope, making the shape.
  // "rounded" makes both caps the same size (H/2). Otherwise "crew" picks which
  // side gets the smaller cap: false = small-left/big-right (PillButton's
  // pill-button SVG), true = small-right/big-left (crew-pill-button SVG).
  static buildPath(W, H, {rounded = false, crew = false} = {}) {
    let rLeft, rRight;
    if (rounded) {
      rLeft = rRight = H / 2;
    } else if (crew) {
      rLeft = H / 2;
      rRight = H * PillGlow.CREW_RIGHT_RADIUS_RATIO;
    } else {
      rRight = H / 2;
      rLeft = H * PillGlow.LEFT_RADIUS_RATIO;
    }
    const cy = H / 2;
    const cxLeft = rLeft;
    const cxRight = W - rRight;
    const dx = cxRight - cxLeft;

    // Direction of the sloped edge; it tilts more when the two caps differ in size.
    let nx = dx > 0 ? -(rRight - rLeft) / dx : 0;
    if (!isFinite(nx) || Math.abs(nx) >= 1) nx = 0;
    const ny = Math.sqrt(Math.max(0, 1 - nx * nx));

    // Points where the straight edges meet each cap (upper = smaller y).
    const luX = cxLeft + rLeft * nx;
    const luY = cy - rLeft * ny;
    const llX = cxLeft + rLeft * nx;
    const llY = cy + rLeft * ny;
    const ruX = cxRight + rRight * nx;
    const ruY = cy - rRight * ny;
    const rlX = cxRight + rRight * nx;
    const rlY = cy + rRight * ny;

    const n = (v) => Math.round(v * 100) / 100;

    return [
      `M ${n(luX)} ${n(luY)}`,
      `L ${n(ruX)} ${n(ruY)}`,
      `A ${n(rRight)} ${n(rRight)} 0 1 1 ${n(rlX)} ${n(rlY)}`, // right cap
      `L ${n(llX)} ${n(llY)}`,
      `A ${n(rLeft)} ${n(rLeft)} 0 0 1 ${n(luX)} ${n(luY)}`, // left cap
      "Z",
    ].join(" ");
  }
}
